import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { updateScraperConfiguration } from '@/lib/api';
import type { AnalyticsScraperConfig, AnalyticsScraperConfigUpdate } from '@sia/analytics-contracts';

const weekdays = [
  ['runOnMonday', 'M', 'Monday'],
  ['runOnTuesday', 'T', 'Tuesday'],
  ['runOnWednesday', 'W', 'Wednesday'],
  ['runOnThursday', 'T', 'Thursday'],
  ['runOnFriday', 'F', 'Friday'],
  ['runOnSaturday', 'S', 'Saturday'],
  ['runOnSunday', 'S', 'Sunday'],
] as const;

function ScraperConfigCard({ config }: { config: AnalyticsScraperConfig }) {
  const [draft, setDraft] = useState<AnalyticsScraperConfigUpdate>(config);
  const mutation = useMutation({
    mutationFn: () => updateScraperConfiguration(config.source, draft),
    onSuccess: (saved) => setDraft(saved),
  });

  function updateField<Key extends keyof AnalyticsScraperConfigUpdate>(key: Key, value: AnalyticsScraperConfigUpdate[Key]) {
    setDraft((current) => ({ ...current, [key]: value }));
    if (mutation.isSuccess) mutation.reset();
  }

  return <form className="scraper-config-card panel" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
    <div className="scraper-config-card__header">
      <div>
        <p className="scraper-config-card__eyebrow">Source</p>
        <h2>{config.source === 'yad2' ? 'Yad2' : 'Facebook'}</h2>
      </div>
      <label className="scraper-config-toggle">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => updateField('enabled', event.currentTarget.checked)}
        />
        <span>Enabled</span>
      </label>
    </div>

    <div className="scraper-config-fields">
      <label>
        <span>Window start</span>
        <input type="time" value={draft.windowStart} onChange={(event) => updateField('windowStart', event.currentTarget.value)} />
      </label>
      <label>
        <span>Window end</span>
        <input type="time" value={draft.windowEnd} onChange={(event) => updateField('windowEnd', event.currentTarget.value)} />
      </label>
      <label>
        <span>Max listing age (days)</span>
        <input type="number" min="1" max="365" step="1" value={draft.maxListingAgeDays} onChange={(event) => updateField('maxListingAgeDays', event.currentTarget.valueAsNumber)} />
      </label>
      <label>
        <span>Hourly frequency</span>
        <input type="number" min="1" max="24" step="1" value={draft.hourlyFrequency} onChange={(event) => updateField('hourlyFrequency', event.currentTarget.valueAsNumber)} />
      </label>
    </div>

    <fieldset className="scraper-config-weekdays">
      <legend>Run on weekdays</legend>
      <div>
        {weekdays.map(([key, letter, label]) => <button
          className={`scraper-config-day${draft[key] ? ' is-selected' : ''}`}
          type="button"
          aria-label={`Run on ${label}`}
          aria-pressed={draft[key]}
          title={label}
          onClick={() => updateField(key, !draft[key])}
          key={key}
        >{letter}</button>)}
      </div>
    </fieldset>

    <div className="scraper-config-card__footer">
      {mutation.isSuccess ? <span className="scraper-config-status">Saved</span> : null}
      {mutation.error ? <span className="scraper-config-status scraper-config-status--error">{mutation.error.message}</span> : null}
      <button className="button button--primary scraper-config-save" type="submit" disabled={mutation.isPending}>Save changes</button>
    </div>
  </form>;
}

export function ScraperConfigEditor({ configs, error }: { configs: AnalyticsScraperConfig[]; error?: string }) {
  const orderedConfigs = [...configs].sort((left) => left.source === 'yad2' ? -1 : 1);
  return <section className="scraper-config-section">
    <div className="scraper-config-section__heading">
      <div>
        <p className="section-label scraper-config-section__eyebrow">Scraper configuration</p>
      </div>
    </div>
    {error ? <p className="form-error">{error}</p> : null}
    {orderedConfigs.length > 0 ? <div className="scraper-config-grid">{orderedConfigs.map((config) => <ScraperConfigCard config={config} key={config.source} />)}</div> : <p className="awaiting">Awaiting configuration</p>}
  </section>;
}
